import {
  createAllComponentSchemasNarrowed,
  getStandardComponent
} from "./chunk-JETULU6Z.js";
import {
  DEFAULT_DOCX_RENDERER_ID,
  DOCX_RENDERER_IDS,
  docxComponentDefinitionName,
  docxPropsSchemaForRenderer
} from "./chunk-HDFGXHUB.js";

// src/schemas/generator.ts
import { Type } from "@sinclair/typebox";
import { latestVersion } from "@json-to-office/shared";
function generateUnifiedDocumentSchema(options = {}) {
  return Type.Union(
    DOCX_RENDERER_IDS.map(
      (renderer) => generateRendererDocumentSchema(
        options,
        renderer,
        renderer !== DEFAULT_DOCX_RENDERER_ID
      )
    ),
    {
      description: "Document definition, discriminated by the optional renderer field. Omitted renderer means docxjs."
    }
  );
}
function generateRendererDocumentSchema(options, renderer, requireDiscriminator) {
  const {
    includeStandardComponents = true,
    customComponents = [],
    title = "JSON Report Definition",
    description = "JSON report definition with TypeBox schemas"
  } = options;
  let capturedRootChildSchemas = [];
  let capturedPluginSchemas = [];
  const definitionName = docxComponentDefinitionName(renderer);
  const ComponentDefinition = Type.Recursive(
    (This) => {
      const pluginSchemas = [];
      for (const customComponent of customComponents) {
        if (customComponent.versionedProps && customComponent.versionedProps.length > 0) {
          const versionEntries = customComponent.versionedProps;
          const latest = latestVersion(versionEntries.map((e) => e.version));
          for (const entry of versionEntries) {
            const versionLiteralDesc = entry.description || customComponent.description;
            const schema = {
              name: Type.Literal(customComponent.name),
              id: Type.Optional(Type.String()),
              version: versionLiteralDesc ? Type.Literal(entry.version, {
                description: versionLiteralDesc
              }) : Type.Literal(entry.version),
              props: entry.propsSchema
            };
            if (entry.hasChildren) {
              schema.children = Type.Optional(Type.Array(This));
            }
            const versionDesc = entry.description ? `${customComponent.name} v${entry.version} \u2014 ${entry.description}` : customComponent.description ? `${customComponent.name} v${entry.version} \u2014 ${customComponent.description}` : `${customComponent.name} v${entry.version}`;
            pluginSchemas.push(
              Type.Object(schema, {
                additionalProperties: false,
                description: versionDesc
              })
            );
          }
          const latestEntry = versionEntries.find((e) => e.version === latest);
          const fallbackSchema = {
            name: Type.Literal(customComponent.name),
            id: Type.Optional(Type.String()),
            props: latestEntry.propsSchema
          };
          if (latestEntry.hasChildren) {
            fallbackSchema.children = Type.Optional(Type.Array(This));
          }
          const fallbackDesc = latestEntry.description ? `${customComponent.name} (latest: v${latest}) \u2014 ${latestEntry.description}` : customComponent.description ? `${customComponent.name} (latest: v${latest}) \u2014 ${customComponent.description}` : `${customComponent.name} (latest: v${latest})`;
          pluginSchemas.push(
            Type.Object(fallbackSchema, {
              additionalProperties: false,
              description: fallbackDesc
            })
          );
        } else {
          const hasChildren = !!customComponent.hasChildren;
          const schema = {
            name: Type.Literal(customComponent.name),
            id: Type.Optional(Type.String()),
            props: customComponent.propsSchema
          };
          if (hasChildren) {
            schema.children = Type.Optional(Type.Array(This));
          }
          pluginSchemas.push(
            Type.Object(schema, {
              additionalProperties: false,
              ...customComponent.description ? { description: customComponent.description } : {}
            })
          );
        }
      }
      const { schemas: standardSchemas, byName } = includeStandardComponents ? createAllComponentSchemasNarrowed(This, pluginSchemas, {
        renderer,
        requireDiscriminator
      }) : { schemas: [], byName: /* @__PURE__ */ new Map() };
      const sectionComponent = getStandardComponent("section");
      capturedRootChildSchemas = [
        "section",
        ...sectionComponent?.allowedChildren ?? []
      ].map((componentName) => byName.get(componentName)).filter((schema) => schema !== void 0);
      capturedPluginSchemas = pluginSchemas;
      const componentSchemas = [...standardSchemas, ...pluginSchemas];
      if (componentSchemas.length === 0) {
        return Type.Any();
      } else if (componentSchemas.length === 1) {
        return componentSchemas[0];
      } else {
        const componentDescription = customComponents.length > 0 ? "Component definition with discriminated union including custom components" : "Component definition with discriminated union";
        return Type.Union(componentSchemas, {
          discriminator: { propertyName: "name" },
          description: componentDescription
        });
      }
    },
    { $id: definitionName }
  );
  const reportComponent = getStandardComponent("docx");
  if (!reportComponent) {
    throw new Error("Docx root component not found in registry");
  }
  if (capturedRootChildSchemas.length === 0) {
    throw new Error(
      "Root child schemas not found in narrowed standard schemas"
    );
  }
  return Type.Object(
    {
      name: Type.Literal("docx"),
      id: Type.Optional(Type.String()),
      $schema: Type.Optional(Type.String({ format: "uri" })),
      renderer: requireDiscriminator ? Type.Literal(renderer, {
        description: "Renderer backend for this document"
      }) : Type.Optional(
        Type.Literal(renderer, {
          description: 'Renderer backend. Omitted defaults to "docxjs".'
        })
      ),
      // Optional so the exported schema agrees with the runtime validator and
      // the generator, both of which treat a propless root as `props: {}`.
      // Schema-driven editors would otherwise flag a document that builds.
      props: Type.Optional(
        docxPropsSchemaForRenderer(
          reportComponent.name,
          reportComponent.propsSchema,
          renderer
        )
      ),
      // Required, matching the runtime validator: a root without `children`
      // is rejected (deep-validator.ts), so the exported schema says so too.
      children: Type.Array(
        Type.Union([...capturedRootChildSchemas, ...capturedPluginSchemas], {
          discriminator: { propertyName: "name" }
        })
      )
    },
    {
      additionalProperties: false,
      title,
      description,
      // Embed it so convertToJsonSchema extracts it to definitions and this
      // branch's bare `$ref: "<definitionName>"` values resolve.
      definitions: { [definitionName]: ComponentDefinition }
    }
  );
}

export {
  generateUnifiedDocumentSchema
};
//# sourceMappingURL=chunk-VLE4CS57.js.map